/**
 * Full-manual PDF export — generates a vector/text PDF (not rasterised),
 * walks the MANUAL_CHAPTERS structure, and produces one section per chapter
 * with a Table of Contents at the top.
 *
 * Why not reuse html2canvas? Because rasterising 30+ DOM pages would yield
 * a 30+ MB blurry PDF. A native jsPDF text document weighs <100 kB and
 * stays selectable / searchable.
 *
 * Diagrams (chapter inline SVGs) are skipped here on purpose — the per-page
 * PDF button still ships the rasterised version of any single page with
 * its diagram. The full-manual export is meant as a reference handout.
 */
import { jsPDF } from 'jspdf';

const M = 16;          // page margins (mm)
const LINE = 5.2;      // base line height (mm)
const FONT_BODY = 10;
const FONT_H1 = 16;
const FONT_H2 = 12;
const FONT_SMALL = 8.5;

function ensureSpace(pdf, y, pageH, needed = LINE) {
  if (y + needed > pageH - M) {
    pdf.addPage();
    return M;
  }
  return y;
}

function writeMultiline(pdf, text, x, y, maxW, lineH = LINE, pageH = 297) {
  const lines = pdf.splitTextToSize(text || '', maxW);
  let cy = y;
  for (const line of lines) {
    cy = ensureSpace(pdf, cy, pageH, lineH);
    pdf.text(line, x, cy);
    cy += lineH;
  }
  return cy;
}

function writeChapterCover(pdf, chapter, page, pageW, pageH) {
  pdf.setFillColor(chapter.color);
  pdf.rect(M, M, pageW - 2 * M, 14, 'F');
  pdf.setTextColor('#FFFFFF');
  pdf.setFontSize(FONT_H1);
  pdf.text(page.title || chapter.title, M + 3, M + 9.5);
  pdf.setTextColor('#0F172A');
  pdf.setFontSize(FONT_BODY);

  let cy = M + 22;
  if (page.intro) {
    cy = writeMultiline(pdf, page.intro, M, cy, pageW - 2 * M, LINE, pageH);
    cy += 2;
  }
  if (Array.isArray(page.bullets)) {
    pdf.setFontSize(FONT_BODY);
    page.bullets.forEach((b) => {
      cy = ensureSpace(pdf, cy, pageH, LINE);
      cy = writeMultiline(pdf, `• ${b}`, M + 2, cy, pageW - 2 * M - 4, LINE, pageH);
    });
  }
  return cy + 2;
}

function writeSection(pdf, title, color, pageW, pageH, y) {
  let cy = ensureSpace(pdf, y, pageH, LINE * 3);
  pdf.setDrawColor(color);
  pdf.setLineWidth(0.6);
  pdf.line(M, cy - 1, pageW - M, cy - 1);
  pdf.setTextColor(color);
  pdf.setFontSize(FONT_H2);
  pdf.text(title, M, cy + 5);
  pdf.setTextColor('#0F172A');
  pdf.setFontSize(FONT_BODY);
  return cy + 9;
}

function writeDefinitions(pdf, items, pageW, pageH, y) {
  let cy = y;
  items.forEach((it) => {
    cy = ensureSpace(pdf, cy, pageH, LINE * 2);
    pdf.setFont(undefined, 'bold');
    pdf.text(`${it.term}.`, M, cy);
    pdf.setFont(undefined, 'normal');
    cy = writeMultiline(pdf, it.def, M, cy + LINE * 0.8, pageW - 2 * M, LINE * 0.95, pageH);
    cy += 1;
  });
  return cy;
}

function writeFormulas(pdf, items, pageW, pageH, y) {
  let cy = y;
  items.forEach((it) => {
    cy = ensureSpace(pdf, cy, pageH, LINE * 3);
    pdf.setFont(undefined, 'bold');
    pdf.text(it.name, M, cy);
    pdf.setFont(undefined, 'italic');
    cy = writeMultiline(pdf, `▸ ${it.formula}`, M, cy + LINE, pageW - 2 * M, LINE * 0.95, pageH);
    pdf.setFont(undefined, 'normal');
    if (it.example) {
      pdf.setFontSize(FONT_SMALL);
      cy = writeMultiline(pdf, `Ex. ${it.example}`, M, cy + 1, pageW - 2 * M, LINE * 0.9, pageH);
      pdf.setFontSize(FONT_BODY);
    }
    cy += 1.5;
  });
  return cy;
}

function writeVocabulary(pdf, items, pageW, pageH, y) {
  let cy = y;
  items.forEach((it) => {
    cy = ensureSpace(pdf, cy, pageH, LINE);
    pdf.setFont(undefined, 'bold');
    pdf.text(`${it.word} :`, M, cy);
    pdf.setFont(undefined, 'normal');
    cy = writeMultiline(pdf, ` ${it.def}`, M + pdf.getTextWidth(`${it.word} : `), cy, pageW - 2 * M - pdf.getTextWidth(`${it.word} : `) - 2, LINE * 0.95, pageH);
    cy += 0.5;
  });
  return cy;
}

function writeListWithIntros(pdf, items, pageW, pageH, y) {
  let cy = y;
  items.forEach((it) => {
    cy = ensureSpace(pdf, cy, pageH, LINE * 2);
    pdf.setFont(undefined, 'bold');
    cy = writeMultiline(pdf, it.title || '', M, cy, pageW - 2 * M, LINE, pageH);
    pdf.setFont(undefined, 'normal');
    if (it.body) {
      cy = writeMultiline(pdf, it.body, M, cy, pageW - 2 * M, LINE * 0.95, pageH);
    }
    cy += 1.5;
  });
  return cy;
}

/**
 * Render the full manual to a PDF and trigger the download.
 * @param {Array} chapters MANUAL_CHAPTERS from data/manualContent.js
 * @param {string} filename Output filename without extension
 */
export async function downloadFullManualPdf(chapters, filename = 'manuel-hst') {
  const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();

  // -------- Cover --------
  pdf.setFillColor('#7C2D12');
  pdf.rect(0, 0, pageW, pageH, 'F');
  pdf.setTextColor('#FFFFFF');
  pdf.setFontSize(34);
  pdf.text('Manuel SCD', pageW / 2, 70, { align: 'center' });
  pdf.setFontSize(13);
  pdf.text('Accompagnement pédagogique', pageW / 2, 80, { align: 'center' });
  pdf.setFontSize(9);
  pdf.text('Société de Commerce et de Distribution', pageW / 2, 92, { align: 'center' });
  pdf.setFontSize(10);
  pdf.text(`${chapters.length} chapitres · ${chapters.reduce((s, c) => s + c.pages.length, 0)} pages`, pageW / 2, 110, { align: 'center' });
  pdf.text(new Date().toLocaleDateString('fr-FR'), pageW / 2, 120, { align: 'center' });

  // -------- Table of Contents --------
  pdf.addPage();
  pdf.setTextColor('#0F172A');
  pdf.setFontSize(FONT_H1);
  pdf.text('Sommaire', M, M + 4);
  pdf.setFontSize(FONT_BODY);
  let cy = M + 18;
  chapters.forEach((c, i) => {
    cy = ensureSpace(pdf, cy, pageH, LINE);
    pdf.setFillColor(c.color);
    pdf.rect(M, cy - 3.8, 3, 3, 'F');
    pdf.text(`${i + 1}. ${c.title}`, M + 6, cy);
    pdf.setTextColor('#64748B');
    pdf.setFontSize(FONT_SMALL);
    pdf.text(`${c.pages.length} pages`, pageW - M, cy, { align: 'right' });
    pdf.setTextColor('#0F172A');
    pdf.setFontSize(FONT_BODY);
    cy += LINE * 1.3;
  });

  // -------- Chapters --------
  chapters.forEach((chapter) => {
    pdf.addPage();
    let y = M;
    chapter.pages.forEach((page, idx) => {
      if (idx > 0) y = ensureSpace(pdf, y + 6, pageH, LINE);
      if (page.type === 'cover') {
        y = writeChapterCover(pdf, chapter, page, pageW, pageH);
      } else if (page.type === 'definitions') {
        y = writeSection(pdf, page.title || 'Définitions', chapter.color, pageW, pageH, y);
        y = writeDefinitions(pdf, page.items || [], pageW, pageH, y);
      } else if (page.type === 'formulas') {
        y = writeSection(pdf, page.title || 'Formules', chapter.color, pageW, pageH, y);
        y = writeFormulas(pdf, page.items || [], pageW, pageH, y);
      } else if (page.type === 'vocabulary') {
        y = writeSection(pdf, page.title || 'Vocabulaire', chapter.color, pageW, pageH, y);
        y = writeVocabulary(pdf, page.items || [], pageW, pageH, y);
      } else if (page.type === 'cases' || page.type === 'pitfalls') {
        y = writeSection(pdf, page.title || '', chapter.color, pageW, pageH, y);
        y = writeListWithIntros(pdf, page.items || [], pageW, pageH, y);
      } else if (page.type === 'diagram') {
        y = writeSection(pdf, page.title || 'Schéma', chapter.color, pageW, pageH, y);
        if (page.intro) y = writeMultiline(pdf, page.intro, M, y, pageW - 2 * M, LINE, pageH);
        pdf.setFont(undefined, 'italic');
        y = writeMultiline(pdf, `[Schéma : ${page.diagram}] — légende : ${(page.legend || []).join(' · ')}`, M, y + 1, pageW - 2 * M, LINE * 0.9, pageH);
        pdf.setFont(undefined, 'normal');
      }
    });
  });

  // -------- Footer page numbers (kept simple - per page) --------
  const total = pdf.internal.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    pdf.setPage(i);
    pdf.setFontSize(FONT_SMALL);
    pdf.setTextColor('#94A3B8');
    pdf.text(`${i} / ${total}`, pageW - M, pageH - 8, { align: 'right' });
    if (i > 1) {
      pdf.text('Manuel SCD', M, pageH - 8);
    }
  }

  pdf.save(`${filename}.pdf`);
}
